import type { StudySelectDto } from '@@types/api.types';
import { type SimpleTab, SimpleTabs } from '@components/shared/simple-tabs.component';
import { Badge } from '@components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { GitBranchIcon, ListChecksIcon, ScaleIcon, TargetIcon, UserRoundCheckIcon } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { type MeiaPhaseKey, type MeiaTabKey, meiaPhaseSteps } from '../meia.constants';
import { MeiaStudyDetailHeader } from './meia-study-detail-header.component';

const tabIcons: Record<MeiaPhaseKey, JSX.Element> = {
  p1: <TargetIcon size={16} />,
  p2: <GitBranchIcon size={16} />,
  p3: <ListChecksIcon size={16} />,
  p4: <ScaleIcon size={16} />,
  p5: <UserRoundCheckIcon size={16} />,
};

type TabEntry = {
  icon: JSX.Element;
  label: JSX.Element;
  name: string;
  value: MeiaPhaseKey;
};

type MeiaStudyHeaderProps = {
  acrSlot?: ReactNode;
  activeTab: MeiaTabKey;
  canEdit: boolean;
  missionsCount: number;
  onEdit: () => void;
  onTabChange: (tab: MeiaTabKey) => void;
  study: StudySelectDto;
  threadsLabel?: string;
};

const phaseTabLabel = (label: string, name: string): JSX.Element => (
  <span className="inline-flex items-center gap-2">
    <span>{name}</span>
    <Badge
      size="xs"
      variant="primary"
    >
      {label}
    </Badge>
  </span>
);

const buildTabs = (): TabEntry[] =>
  meiaPhaseSteps.map<TabEntry>((phase) => ({
    icon: tabIcons[phase.key],
    label: phaseTabLabel(phase.label, phase.name),
    name: `${phase.label} ${phase.name}`,
    value: phase.key,
  }));

export const MeiaStudyHeader: FC<MeiaStudyHeaderProps> = (props) => {
  const { acrSlot, activeTab, canEdit, missionsCount, onEdit, onTabChange, study, threadsLabel } = props;

  const tabs = buildTabs();
  const simpleTabs: SimpleTab[] = tabs.map((tab) => ({
    icon: tab.icon,
    label: tab.label,
    value: tab.value,
  }));
  const activeTabEntry = tabs.find((tab) => tab.value === activeTab);
  const activeTabName = activeTabEntry?.name ?? 'Overview';

  const handleTabChange = (value: string): void => {
    onTabChange(value as MeiaTabKey);
  };

  return (
    <MeiaStudyDetailHeader
      acrSlot={acrSlot}
      canEdit={canEdit}
      missionsCount={missionsCount}
      onEdit={onEdit}
      phaseLabel={activeTabEntry?.name}
      study={study}
      threadsLabel={threadsLabel}
    >
      <div className="pb-4 @min-[62.5rem]/study-detail-header:hidden">
        <Select
          onValueChange={handleTabChange}
          value={activeTabEntry ? activeTab : ''}
        >
          <SelectTrigger className="w-full bg-white">
            <SelectValue placeholder="Select tab">{activeTabName}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {tabs.map((tab) => (
              <SelectItem
                key={tab.value}
                value={tab.value}
              >
                {tab.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="hidden overflow-x-auto overscroll-x-contain touch-pan-x @min-[62.5rem]/study-detail-header:block">
        <SimpleTabs
          activeTab={activeTab}
          className="min-w-max"
          fullWidth={false}
          onTabChange={handleTabChange}
          resetScrollOnNavigation={false}
          tabs={simpleTabs}
          variant="attached"
        />
      </div>
    </MeiaStudyDetailHeader>
  );
};
